import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { WeddingBooking, WeddingPackage } from '../types';
import toast from 'react-hot-toast';

interface BookingIntent {
  vendorId: string;
  bookingDate: Date;
  bookingType: 'full_day' | 'morning' | 'evening';
  totalPrice: number;
  packageId?: string;
  userNote?: string;
  guestCount?: number;
}

export const useVendorAvailability = (vendorId: string, month: Date) => {
  return useQuery({
    queryKey: ['vendorAvailability', vendorId, month.getMonth(), month.getFullYear()],
    queryFn: async () => {
      // Helper to get local date string YYYY-MM-DD
      const toLocalISO = (d: Date) => {
        const offset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - offset).toISOString().split('T')[0];
      };

      const startOfMonth = toLocalISO(new Date(month.getFullYear(), month.getMonth(), 1));
      const endOfMonth = toLocalISO(new Date(month.getFullYear(), month.getMonth() + 1, 0));

      const { data, error } = await supabase
        .from('wedding_bookings')
        .select('booking_date, booking_type')
        .eq('vendor_id', vendorId)
        .gte('booking_date', startOfMonth)
        .lte('booking_date', endOfMonth)
        .in('status', ['confirmed', 'pending']);

      if (error) throw error;
      return data as { booking_date: string; booking_type: 'full_day' | 'morning' | 'evening' }[];
    },
    enabled: !!vendorId,
  });
};

export const useVendorPackages = (vendorId: string) => {
  return useQuery({
    queryKey: ['vendorPackages', vendorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wedding_packages')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('price', { ascending: true });
        
      if (error) throw error;
      return data as WeddingPackage[];
    },
    enabled: !!vendorId
  });
};

export const useCreateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (intent: BookingIntent) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('You must be logged in to book');

      // 1. Check availability via RPC (double check)
      const { data: isAvailable, error: checkError } = await supabase.rpc(
        'check_vendor_availability',
        {
          vendor_uuid: intent.vendorId,
          // Use local date string to avoid timezone shifts
          check_date: new Date(intent.bookingDate.getTime() - (intent.bookingDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0],
          requested_type: intent.bookingType
        }
      );

      if (checkError) throw checkError;
      if (!isAvailable) throw new Error('Selected date/slot is no longer available');

      // 2. Create Booking
      const payload: any = {
          vendor_id: intent.vendorId,
          user_id: session.user.id,
          booking_date: new Date(intent.bookingDate.getTime() - (intent.bookingDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0],
          booking_type: intent.bookingType,
          total_price: intent.totalPrice,
          package_id: intent.packageId || null,
          user_note: intent.userNote || null,
          status: 'pending' 
      };

      if (intent.guestCount) {
        payload.guest_count = intent.guestCount;
      }

      const { data, error } = await supabase
        .from('wedding_bookings')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { vendorId }) => {
      toast.success('Booking request sent successfully!');
      queryClient.invalidateQueries({ queryKey: ['vendorAvailability', vendorId] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create booking');
    },
  });
};
